import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import {
  faShieldCheck,
  faTriangleExclamation
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Table } from '@morressier/alembic';
import {
  EMAIL_DELIVERY_STATUS_MAP,
  SubmissionParticipantUser
} from '@publishing-engine/domain/models';
import { useState } from 'react';
import { EmailDeliveryStatus } from 'src/components/User/EmailView';
import { formatDateAndTime } from 'src/utils/date';
import SubmittedAuthorDetailsModal from '../AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';

type ParticipantTableProps = {
  Participants: SubmissionParticipantUser[];
};
interface TableColumn {
  key: string;
  label: string;
  size: string | number;
  render?: (
    participant: SubmissionParticipantUser,
    onSelectedAuthor: (author: SubmissionParticipantUser | null) => void
  ) => React.ReactNode;
}
const REVIEW_TABLE_COLUMNS = (): TableColumn[] => [
  {
    key: 'Name',
    label: 'Name',
    size: 2,
    render: (participant, onSelectedAuthor) => {
      const IsInvitedUser =
        participant.firstName === 'Invited' && participant.lastName === 'User';
      return (
        <>
          <Box onClick={() => onSelectedAuthor(participant)} cursor="pointer">
            {!IsInvitedUser && (
              <Tooltip
                placement="top"
                label={`${participant.firstName} ${participant.lastName}`}
                hasArrow
              >
                <Text
                  textStyle="actionLabel"
                  color="actionPrimaryFill"
                  noOfLines={1}
                >{`${participant.firstName} ${participant.lastName}`}</Text>
              </Tooltip>
            )}

            <Tooltip placement="top" label={participant.email} hasArrow>
              <Text
                textStyle={IsInvitedUser ? 'actionLabel' : undefined}
                color={IsInvitedUser ? 'actionPrimaryFill' : undefined}
                noOfLines={1}
              >
                {participant.email}
              </Text>
            </Tooltip>
          </Box>{' '}
          {participant.isSubmittingAuthor && (
            <Box mt={2}>
              <Badge variant="attention" size="context">
                Submitting Author
              </Badge>
            </Box>
          )}
          {participant.isCorrespondingAuthor && (
            <Box mt={2}>
              <Badge variant="attention" size="context">
                Corresponding Author
              </Badge>
            </Box>
          )}{' '}
        </>
      );
    }
  },

  {
    key: 'Verified',
    label: 'Verified',
    size: 1,
    render: participant => (
      <>
        {participant.isEmailDelivered ? (
          <Flex gap={2}>
            <Tooltip
              placement="top"
              label={` Email verified`}
              shouldWrapChildren
            >
              <Badge variant="success">
                <FontAwesomeIcon icon={faShieldCheck} />
              </Badge>
            </Tooltip>
            Yes
          </Flex>
        ) : (
          <Flex gap={2}>
            <Tooltip
              placement="top"
              label={`Email Unverified`}
              shouldWrapChildren
            >
              <Badge variant="attention">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </Badge>
            </Tooltip>
            No
          </Flex>
        )}
      </>
    )
  },

  {
    key: 'status',
    label: 'Last email status',
    size: 1.5,
    render: participant => {
      const deliveryIconColor = participant.lastSentEmailStatus
        ? EMAIL_DELIVERY_STATUS_MAP[
            participant.lastSentEmailStatus as EmailDeliveryStatus
          ]
        : undefined;

      return (
        <>
          <Badge variant={deliveryIconColor}>
            <Text textTransform={'capitalize'}>
              {participant.lastSentEmailStatus ?? '--'}{' '}
            </Text>
          </Badge>
        </>
      );
    }
  },
  {
    key: 'Last Email Sent',
    label: 'Last Email Sent On',
    size: 1.5,
    render: participant => (
      <>
        {participant.lastSentEmailTimeStamp
          ? formatDateAndTime(
              new Date(participant.lastSentEmailTimeStamp * 1000)
            )
          : '--'}
      </>
    )
  }
];
const tableColumns = REVIEW_TABLE_COLUMNS();
const TableHeader = ({ columns }: { columns: TableColumn[] }) => (
  <Table.HeaderRow>
    {columns.map(column => (
      <Table.HeaderCell
        key={column.key}
        px={1}
        flex={column.size}
        textAlign="left"
      >
        {column.label}
      </Table.HeaderCell>
    ))}
  </Table.HeaderRow>
);
const ParticipantTable = ({ Participants }: ParticipantTableProps) => {
  const [selectedAuthor, setSelectedAuthor] =
    useState<SubmissionParticipantUser | null>(null);

  return (
    <>
      <Table>
        <TableHeader columns={tableColumns} />
        <Table.Body>
          {Participants.map((participant, index) => {
            return (
              <>
                <Table.Row key={index}>
                  {tableColumns.map(column => (
                    <>
                      <Table.RowCell
                        key={column.key}
                        flex={column.size}
                        textAlign="left"
                        px={1}
                        py={2}
                        height={'full'}
                      >
                        {column.render
                          ? column.render(participant, setSelectedAuthor)
                          : null}
                      </Table.RowCell>
                    </>
                  ))}
                </Table.Row>
              </>
            );
          })}
        </Table.Body>
      </Table>
      {selectedAuthor && (
        <SubmittedAuthorDetailsModal
          userId={selectedAuthor?.id || ''}
          onBackClick={() => setSelectedAuthor(null)}
          backButtonText="Back to Email Verification"
          showReviewerActivity={true}
          roleContext="editorial"
        />
      )}
    </>
  );
};

export default ParticipantTable;
